import type { FilterSelection } from '@enamelai/design-system'
import { IClinicE911Address, IClinicPhoneDevice } from '@dataTypes/clinic.type'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { getFullName } from '@utils/fullname.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  deletePhoneDeviceAction,
  fetchPhoneManagementData,
  ringPhoneDeviceAction,
} from '@store/actions/settings-communication.action'
import { fetchAllProviders } from '@store/actions/providermanagement.action'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useClinicChange from '../shared/useClinicChange'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  callRecordingModeFromBooleans,
  formatCallRecordingMode,
} from '@utils/clinic.util'

const DEVICES_LOADING_CONTAINER = '#settings-devices'

export type GridRow = {
  extension_display: string
  device: IClinicPhoneDevice
  device_name: string
  type_label: string
  linked_display: string
  team: string
  assigned_display: string
  recording_summary: string[]
}

export const DEVICE_TYPE_LABEL: Record<string, string> = {
  soft_phone: 'Soft phone',
  desk_phone: 'Desk phone',
  voip_phone: 'VoIP phone',
  mobile_phone: 'Mobile phone',
}

export const RECORDING_QUICK_FILTER_INBOUND = 'Inbound calls'
export const RECORDING_QUICK_FILTER_OUTBOUND = 'Outbound calls'

const deviceMatchesMainFilters = (
  d: IClinicPhoneDevice,
  filters: FilterSelection,
): boolean => {
  const typeSel = filters.type
  if (typeSel?.length) {
    const label = DEVICE_TYPE_LABEL[d.device_type] || d.device_type
    if (!typeSel.includes(label)) return false
  }

  const deptSel = filters.team
  if (deptSel?.length) {
    const dept = d.team?.trim() || ''
    if (!deptSel.includes(dept)) return false
  }

  const recSel = filters.recording
  if (recSel?.length) {
    const inboundOn = !!d.record_inbound_calls
    const outboundOn = !!d.record_outbound_calls
    const matchesInbound =
      recSel.includes(RECORDING_QUICK_FILTER_INBOUND) && inboundOn
    const matchesOutbound =
      recSel.includes(RECORDING_QUICK_FILTER_OUTBOUND) && outboundOn
    if (!matchesInbound && !matchesOutbound) return false
  }

  return true
}

const formatLinked = (v: string | null | undefined): string => {
  if (!v) return '—'
  try {
    const d = new Date(v)
    if (Number.isNaN(d.getTime())) return v
    return d.toLocaleDateString()
  } catch {
    return v
  }
}

export interface IDeviceHooks {
  clinicId: string
  dataReady: boolean
  mainFilters: FilterSelection
  mainSearchQuery: string
  onAddDevice: () => void
  onDeleteDevice: (d: IClinicPhoneDevice) => Promise<void>
  onEditDevice: (d: IClinicPhoneDevice) => void
  onHideE911: () => void
  onRingDevice: (d: IClinicPhoneDevice) => Promise<void>
  onViewE911: (e911Id: string | null | undefined) => void
  pendingDelete: IClinicPhoneDevice | null
  phoneDevices: IClinicPhoneDevice[]
  rows: GridRow[]
  selectedDevice: IClinicPhoneDevice | null
  selectedE911: IClinicE911Address | null
  setMainFilters: Dispatch<SetStateAction<FilterSelection>>
  setMainSearchQuery: Dispatch<SetStateAction<string>>
  setPendingDelete: Dispatch<SetStateAction<IClinicPhoneDevice | null>>
  setSelectedDevice: Dispatch<SetStateAction<IClinicPhoneDevice | null>>
  setShowAddDevice: Dispatch<SetStateAction<boolean>>
  showAddDevice: boolean
}

const useDevice = (): IDeviceHooks => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''
  const phoneDevices = useAppSelector(
    (s) => s.settingsCommunication.phoneDevices,
  )
  const e911Addresses = useAppSelector(
    (s) => s.settingsCommunication.e911Addresses,
  )
  const providers = useAppSelector((s) => s.provider.data)

  const [mainSearchQuery, setMainSearchQuery] = useState('')
  const [mainFilters, setMainFilters] = useState<FilterSelection>({})
  const [pendingDelete, setPendingDelete] = useState<IClinicPhoneDevice | null>(
    null,
  )
  const [showAddDevice, setShowAddDevice] = useState(false)
  const [selectedDevice, setSelectedDevice] =
    useState<IClinicPhoneDevice | null>(null)
  const [selectedE911, setSelectedE911] = useState<IClinicE911Address | null>(
    null,
  )
  const [dataReady, setDataReady] = useState(false)
  const loadGen = useRef(0)

  useClinicChange(clinicId, () => {
    loadGen.current += 1
    setDataReady(false)
    setShowAddDevice(false)
    setSelectedDevice(null)
  })

  useSettingsSubpageLoading(!dataReady)

  useEffect(() => {
    const gen = ++loadGen.current
    if (!clinicId) {
      setDataReady(true)
      return
    }
    void (async (): Promise<void> => {
      try {
        await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
        if (!providers.length) {
          void dispatch(fetchAllProviders())
        }
      } finally {
        if (loadGen.current === gen) setDataReady(true)
      }
    })()
  }, [clinicId, dispatch, providers.length])

  const userNameById = useCallback(
    (userId: string | null | undefined): string => {
      if (!userId) return '—'
      const p = providers.find((x) => x.user_id === userId)
      return p ? getFullName(p) : userId.slice(0, 8)
    },
    [providers],
  )

  const rows: GridRow[] = useMemo(() => {
    const qRaw = mainSearchQuery.trim().toLowerCase()
    const tokens =
      qRaw.length === 0 ? [] : qRaw.split(/\s+/).filter((t) => t.length > 0)

    return phoneDevices
      .filter((d) => {
        if (!deviceMatchesMainFilters(d, mainFilters)) return false

        if (tokens.length === 0) return true

        const hay = [
          d.device_name,
          DEVICE_TYPE_LABEL[d.device_type] || d.device_type,
          formatLinked(d.linked_at),
          d.team,
          userNameById(d.assigned_user_id),
          d.device_extension_number,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return tokens.every((t) => hay.includes(t))
      })
      .map((d) => {
        const mode = callRecordingModeFromBooleans(
          d.record_inbound_calls,
          d.record_outbound_calls,
        )
        return {
          device: d,
          device_name: d.device_name || '—',
          type_label: DEVICE_TYPE_LABEL[d.device_type] || d.device_type,
          linked_display: formatLinked(d.linked_at),
          team: d.team?.trim() || '—',
          assigned_display: userNameById(d.assigned_user_id),
          extension_display: d.device_extension_number?.trim() || '—',
          recording_summary: formatCallRecordingMode(mode),
        }
      })
  }, [mainFilters, mainSearchQuery, phoneDevices, userNameById])

  const onAddDevice = useCallback(() => {
    setSelectedDevice(null)
    setShowAddDevice(true)
  }, [])

  const onEditDevice = useCallback((d: IClinicPhoneDevice) => {
    setSelectedDevice(d)
    setShowAddDevice(true)
  }, [])

  const onDeleteDevice = useCallback(
    async (d: IClinicPhoneDevice) => {
      if (!d.clinic_phone_device_id) return

      showLoading({ container: DEVICES_LOADING_CONTAINER })
      try {
        const res = await dispatch(
          deletePhoneDeviceAction({
            id: d.clinic_phone_device_id,
            clinic_id: clinicId,
          }),
        )
        if (deletePhoneDeviceAction.fulfilled.match(res)) {
          showToast(createSuccessToast('Deleted', 'Device removed.'))
        } else {
          showToast(
            createFailedToast(
              'Devices',
              String(res.payload || 'Delete failed.'),
            ),
          )
        }
      } finally {
        hideLoading(DEVICES_LOADING_CONTAINER)
      }
    },
    [clinicId, dispatch, hideLoading, showLoading, showToast],
  )

  const onRingDevice = useCallback(
    async (d: IClinicPhoneDevice) => {
      if (!clinicId || !d.clinic_phone_device_id) {
        showToast(
          createFailedToast(
            'Devices',
            'Select a clinic and save the device first.',
          ),
        )
        return
      }
      if (d.device_type === 'voip_phone') {
        const hasSip = !!(d.sip_uri && String(d.sip_uri).trim())
        const hasPstn = !!(d.pstn_number && String(d.pstn_number).trim())
        if (!hasSip && !hasPstn) {
          showToast(
            createFailedToast(
              'Ring device',
              'Add a PSTN fallback number or provision SIP before ringing this VoIP device.',
            ),
          )
          return
        }
      }

      showLoading({ container: DEVICES_LOADING_CONTAINER })
      try {
        const res = await dispatch(
          ringPhoneDeviceAction({
            id: d.clinic_phone_device_id,
            clinic_id: clinicId,
          }),
        )
        if (ringPhoneDeviceAction.fulfilled.match(res)) {
          showToast(
            createSuccessToast(
              'Ringing',
              'A diagnostic call was placed to this device.',
            ),
          )
        } else {
          showToast(
            createFailedToast(
              'Ring device',
              String(res.payload || 'Failed to ring device.'),
            ),
          )
        }
      } finally {
        hideLoading(DEVICES_LOADING_CONTAINER)
      }
    },
    [clinicId, dispatch, hideLoading, showLoading, showToast],
  )

  const onViewE911 = useCallback(
    (e911Id: string | null | undefined) => {
      if (!e911Id) return null
      const row = e911Addresses.find((e) => e.clinic_e911_address_id === e911Id)
      if (!row) return null
      setSelectedE911(row)
    },
    [e911Addresses],
  )

  const onHideE911 = useCallback(() => {
    setSelectedE911(null)
  }, [])

  return {
    clinicId,
    dataReady,
    mainFilters,
    mainSearchQuery,
    onAddDevice,
    onDeleteDevice,
    onEditDevice,
    onHideE911,
    onRingDevice,
    onViewE911,
    phoneDevices,
    pendingDelete,
    rows,
    selectedDevice,
    selectedE911,
    setMainFilters,
    setMainSearchQuery,
    setPendingDelete,
    setSelectedDevice,
    setShowAddDevice,
    showAddDevice,
  }
}

export default useDevice
